/* eslint-disable react/prop-types */
import FilterHeader from '@/components/core/async-data-table/filter-header';
import { SearchProps } from '@/components/core/async-data-table/type';
import { OrderDatePicker } from '@/components/date/FixedOrderDatePicker';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { convertDateYYYYMMDD } from '@/constants/constant-function';
import { ReactElement, useEffect, useRef, useState } from 'react';

const OrderFilterHeader: React.FC<SearchProps> = ({
  filterColumn = [],
  searchFunction,
  searchValue,
  propSearch,
  setPropSearch,
}): ReactElement => {
  const [orderDate, setOrderDate] = useState<Date | undefined>(undefined);
  const [printedOnly, setPrintedOnly] = useState<boolean>(false);
  const [cashOnDeliveryOnly, setCashOnDeliveryOnly] = useState<boolean>(false);
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const hasInitialized = useRef(false);

  // Initialize date picker from existing created_at filter (if any)
  useEffect(() => {
    if (!hasInitialized.current) {
      const existingDateProp = propSearch.find((p: any) => p.propName === 'created_at');
      if (existingDateProp && existingDateProp.value) {
        const dateValue = existingDateProp.value as string;
        const parsedDate = new Date(dateValue);
        if (!isNaN(parsedDate.getTime())) {
          setOrderDate(parsedDate);
        }
      } else {
        setOrderDate(undefined);
      }
      hasInitialized.current = true;
    }
  }, [propSearch]);

  // Sync date picker with existing date filter from propSearch (after initialization)
  useEffect(() => {
    if (hasInitialized.current) {
      const existingDateProp = propSearch.find((p: any) => p.propName === 'created_at');
      if (existingDateProp && existingDateProp.value) {
        const dateValue = existingDateProp.value as string;
        const parsedDate = new Date(dateValue);
        if (!isNaN(parsedDate.getTime())) {
          setOrderDate(parsedDate);
        }
      } else {
        setOrderDate(undefined);
      }
    }
  }, [propSearch]);

  useEffect(() => {
    const printedProp = propSearch.find((p) => p.propName === 'printed');
    setPrintedOnly(printedProp ? Boolean(printedProp.value) : false);

    const codProp = propSearch.find((p) => p.propName === 'cash_on_delivery');
    setCashOnDeliveryOnly(codProp ? Boolean(codProp.value) : false);

    const sourceProp = propSearch.find((p) => p.propName === 'source');
    setSelectedSource(sourceProp && sourceProp.value ? String(sourceProp.value) : 'all');
  }, [propSearch]);

  const handleDateChange = (date: Date | undefined) => {
    setOrderDate(date);
    if (!date) {
      setPropSearch((prev: any) => prev.filter((p: any) => p.propName !== 'created_at'));
      return;
    }
    const formatted = convertDateYYYYMMDD(date);
    setPropSearch((prev: any) => {
      const updated = prev.filter((p: any) => p.propName !== 'created_at');
      return [...updated, { propName: 'created_at', value: formatted, type: 'date' }];
    });
  };

  const handlePrintedChange = (checked: boolean) => {
    setPrintedOnly(checked);
    setPropSearch((prev: any) => {
      const without = prev.filter((p: any) => p.propName !== 'printed');
      return checked ? [...without, { propName: 'printed', value: true, type: 'boolean' }] : without;
    });
  };

  const handleCashOnDeliveryChange = (checked: boolean) => {
    setCashOnDeliveryOnly(checked);
    setPropSearch((prev: any) => {
      const without = prev.filter((p: any) => p.propName !== 'cash_on_delivery');
      return checked ? [...without, { propName: 'cash_on_delivery', value: true, type: 'boolean' }] : without;
    });
  };

  const handleSourceChange = (sourceValue: string) => {
    setSelectedSource(sourceValue);
    setPropSearch((prev: any) => {
      const without = prev.filter((p: any) => p.propName !== 'source');
      return sourceValue === 'all'
        ? without
        : [...without, { propName: 'source', value: sourceValue, type: 'string' }];
    });
  };

  return (
    <div className="flex flex-col gap-2 md:gap-4 mb-3 md:mb-6">
      {/* Top Row: Search and Date */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 md:gap-3">
        {/* Filter Header (Search Box) - Grows to fill space */}
        <div className="flex-1 w-full order-1 lg:order-1">
          <FilterHeader
            filterColumn={filterColumn}
            searchFunction={searchFunction}
            searchValue={searchValue}
            propSearch={propSearch}
            setPropSearch={setPropSearch}
          />
        </div>

        {/* Order Date Picker - Fixed width on desktop, full width on mobile */}
        <div className="w-full lg:w-[240px] order-2 lg:order-2">
          <OrderDatePicker
            date={orderDate}
            onDateChange={handleDateChange}
            placeholder="Select order date"
          />
        </div>
      </div>

      {/* Bottom Row: Switches & Source Quick Filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-0 md:px-1">
        <div className="flex flex-row items-center gap-3 md:gap-6">
          <div className="flex items-center gap-1.5 md:gap-2 cursor-pointer">
            <Switch
              id="printed-switch"
              checked={printedOnly}
              onCheckedChange={handlePrintedChange}
              className="scale-75 md:scale-100"
            />
            <Label htmlFor="printed-switch" className="text-[10px] md:text-sm font-medium whitespace-nowrap">
              Printed
            </Label>
          </div>

          <div className="flex items-center gap-1.5 md:gap-2 cursor-pointer">
            <Switch
              id="cod-switch"
              checked={cashOnDeliveryOnly}
              onCheckedChange={handleCashOnDeliveryChange}
              className="scale-75 md:scale-100"
            />
            <Label htmlFor="cod-switch" className="text-[10px] md:text-sm font-medium whitespace-nowrap">
              COD
            </Label>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { id: 'all', label: 'All Sources' },
            { id: 'telegram', label: 'Telegram' },
            { id: 'facebook', label: 'Facebook' },
            { id: 'pos', label: 'POS' },
          ].map((item) => {
            const active = selectedSource === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSourceChange(item.id)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                  active
                    ? item.id === 'telegram'
                      ? 'bg-sky-600 text-white border-sky-600'
                      : 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrderFilterHeader;
